import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from './dragModifiers';
import { CSS } from '@dnd-kit/utilities';
import { SelectPopover } from '../../../../components/SelectPopover';
import type { RosterSettings, ScholarshipPlayer } from '../../../../types/scholarships';
import type { RosterSport } from '../../../../constants/sport';
import { tourAnchor } from '../../../tours/tourAnchors';
import { DragHandleIcon } from './icons';
import {
  formatPitchShapes,
  sportHasArmAngle,
  withCurrentOption,
  type PitcherHand,
} from './rosterPalette';
import { HandednessName, IndicatorDots } from './RosterPlayerMarks';
import { compactClassLabel } from './scholarshipClasses';
import { rowActionLabel } from './openableProspect';

interface Props {
  hand: PitcherHand;
  /** The primary pitchers at this hand, in staff order. */
  pitchers: ScholarshipPlayer[];
  /**
   * Two-way players at this hand by their secondary position. Listed after the staff and never
   * dragged: the reorder endpoint is per primary position, and this is not theirs.
   */
  twoWay?: ScholarshipPlayer[];
  settings: RosterSettings;
  /** Softball drops the arm angle column. */
  sport: RosterSport;
  /** Rows whose role save is in flight; their role select is disabled until it lands. */
  savingIds: ReadonlySet<string>;
  onReorder: (hand: PitcherHand, orderedIds: string[]) => void;
  onRoleChange: (pitcher: ScholarshipPlayer, role: string | null) => void;
  onOpenPlayer: (pitcher: ScholarshipPlayer) => void;
  /** This table's first row carries the drag and role tour anchors. */
  ownsAnchors: boolean;
}

const EMPTY_TEXT: Record<PitcherHand, string> = {
  LHP: 'No left-handed pitchers on this board yet.',
  RHP: 'No right-handed pitchers on this board yet.',
};

interface RowProps {
  pitcher: ScholarshipPlayer;
  index: number;
  hand: PitcherHand;
  settings: RosterSettings;
  showArmAngle: boolean;
  saving: boolean;
  draggable: boolean;
  /** Listed by secondary position: no staff order, so the order cell reads "2-way". */
  twoWay?: boolean;
  anchors: boolean;
  onRoleChange: (pitcher: ScholarshipPlayer, role: string | null) => void;
  onOpenPlayer: (pitcher: ScholarshipPlayer) => void;
}

const PitchingStaffTableRow = ({
  pitcher,
  index,
  hand,
  settings,
  showArmAngle,
  saving,
  draggable,
  twoWay = false,
  anchors,
  onRoleChange,
  onOpenPlayer,
}: RowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: pitcher.id,
    disabled: !draggable,
  });
  const name = `${pitcher.firstName} ${pitcher.lastName}`;
  const roleSelectId = `pitching-role-${hand}-${pitcher.id}`;
  return (
    <tr
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : undefined,
      }}
    >
      <td className="pitching-table__handle-cell">
        {draggable && (
          <button
            type="button"
            className="roster-drag-handle"
            aria-label={`Drag to reorder ${name}`}
            {...(anchors ? tourAnchor('rosters.pitching.drag') : {})}
            {...attributes}
            {...listeners}
          >
            <DragHandleIcon />
          </button>
        )}
      </td>
      <td className="pitching-table__order">{twoWay ? '2-way' : index + 1}</td>
      <td>
        {/* Dots lead the name, as on every other roster view. A column of their own cost the
            table ~90px and was what pushed the LHP and RHP tables off a laptop screen. */}
        <span className="pitching-table__player">
          <IndicatorDots indicatorIds={pitcher.indicatorIds} settings={settings} />
          <button
            type="button"
            className="roster-name-button"
            onClick={() => onOpenPlayer(pitcher)}
            aria-label={rowActionLabel(pitcher, name)}
          >
            <HandednessName name={name} bats={pitcher.bats} settings={settings} />
          </button>
        </span>
      </td>
      <td>{compactClassLabel(pitcher) ?? '—'}</td>
      <td>{pitcher.velo || '—'}</td>
      {showArmAngle && <td>{pitcher.armAngle || '—'}</td>}
      <td>{formatPitchShapes(pitcher.pitchShapes) || '—'}</td>
      <td {...(anchors ? tourAnchor('rosters.pitching.role') : {})}>
        <label htmlFor={roleSelectId} className="roster-sr-only">
          Role for {name}
        </label>
        <SelectPopover
          id={roleSelectId}
          options={withCurrentOption(settings.pitchingRoles, pitcher.pitchingRole)}
          value={pitcher.pitchingRole ?? ''}
          onChange={(next) => onRoleChange(pitcher, next === '' ? null : next)}
          placeholder="No role"
          disabled={saving}
          testId={`pitching-role-${pitcher.id}`}
        />
      </td>
    </tr>
  );
};

/**
 * One hand of the staff: LHP or RHP. Rows sort by drag within the table (the reorder endpoint is
 * per position, and a pitcher's hand is their position), and the role saves as soon as it is picked.
 */
export const PitchingStaffTable = ({
  hand,
  pitchers,
  twoWay = [],
  settings,
  sport,
  savingIds,
  onReorder,
  onRoleChange,
  onOpenPlayer,
  ownsAnchors,
}: Props) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const headingId = `pitching-${hand}-heading`;
  const draggable = pitchers.length > 1;
  const showArmAngle = sportHasArmAngle(sport);

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const oldIndex = pitchers.findIndex((p) => p.id === active.id);
    const newIndex = pitchers.findIndex((p) => p.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(hand, arrayMove(pitchers, oldIndex, newIndex).map((p) => p.id));
  };

  return (
    <section className="pitching-table" aria-labelledby={headingId}>
      <h2 id={headingId} className="pitching-table__title">
        {hand} <span className="pitching-table__count">({pitchers.length + twoWay.length})</span>
      </h2>
      {pitchers.length + twoWay.length === 0 ? (
        <p className="pitching-table__empty">{EMPTY_TEXT[hand]}</p>
      ) : (
        <div className="pitching-table__scroll">
          <DndContext
            id={`pitching-${hand}`}
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={pitchers.map((p) => p.id)} strategy={verticalListSortingStrategy}>
              <table className="roster-table">
                <thead>
                  <tr>
                    <th aria-label="Reorder" />
                    <th>#</th>
                    <th>Player</th>
                    <th>Year</th>
                    <th>Velo</th>
                    {showArmAngle && <th className="pitching-table__wrap-head">Arm angle</th>}
                    <th>Shape</th>
                    <th>Role</th>
                  </tr>
                </thead>
                <tbody>
                  {pitchers.map((pitcher, index) => (
                    <PitchingStaffTableRow
                      key={pitcher.id}
                      pitcher={pitcher}
                      index={index}
                      hand={hand}
                      settings={settings}
                      showArmAngle={showArmAngle}
                      saving={savingIds.has(pitcher.id)}
                      draggable={draggable}
                      anchors={ownsAnchors && index === 0}
                      onRoleChange={onRoleChange}
                      onOpenPlayer={onOpenPlayer}
                    />
                  ))}
                  {twoWay.map((pitcher, index) => (
                    <PitchingStaffTableRow
                      key={pitcher.id}
                      pitcher={pitcher}
                      index={pitchers.length + index}
                      hand={hand}
                      settings={settings}
                      showArmAngle={showArmAngle}
                      saving={savingIds.has(pitcher.id)}
                      draggable={false}
                      twoWay
                      anchors={ownsAnchors && pitchers.length === 0 && index === 0}
                      onRoleChange={onRoleChange}
                      onOpenPlayer={onOpenPlayer}
                    />
                  ))}
                </tbody>
              </table>
            </SortableContext>
          </DndContext>
        </div>
      )}
    </section>
  );
};
